import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { brandFontFamily, brandInputTextColor, brandPrimaryColor } from "../../utils/variables";
import useAuth from "../../hooks/useAuth";
import BaseWrapper from "../../components/ui/BaseWrapper";

import { useNavigate, useLocation, useParams } from "react-router-dom";
import routes from "../../routes";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import BaseAlert from "../../components/ui/BaseAlert";
import dayjs from "dayjs";
import BaseButton from "../../components/ui/BaseButton";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import { declineOffer, fetchCustomerCounterOffer } from "../../services/api/offers";
import { showToast } from "../../store/toast/toastSlice";
import ChatTab from "./Tabs/ChatTab";
import {
  CANCELLATION_REASON_LABELS,
  IncompleteWorkOrderReasonType,
  WorkOrderStatusType,
} from "../../utils/enum";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const CustomTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  cursor: "pointer",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },
  height: "34px",
  width: "170px",
  fontWeight: 300,
  fontSize: "15px",
  boxShadow: "0px -1px 8px 0px rgba(0, 0, 0, 0.09)",

  borderRadius: "6px",
  // padding: 0,
  margin: "14px 0",
  background: "white",
  marginRight: theme.spacing(1),
  color: brandInputTextColor,
  fontFamily: brandFontFamily,
  "&:hover": {
    /* color: "#40a9ff",
        opacity: 1, */
  },
  "&.Mui-selected": {
    color: brandPrimaryColor,
    fontWeight: 400,
    borderBottom: `1px solid ${brandPrimaryColor}`,
  },

  "&.Mui-focusVisible": {
    //   backgroundColor: "#d1eaff",
  },
}));

const WorkJobTabs = ({ userAllowedAction }) => {
  const { id } = useParams();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { authRole } = useAuth();
  const [value, setValue] = useState(0);
  const [removeFromListConfirmOpen, setRemoveFromListConfirmOpen] = useState(false);

  const { isPending: isRemoveFromListLoading, mutateAsync: declineOfferMutation } = useMutation({
    mutationFn: declineOffer,
  });

  // The counter offer a client sent the customer. The customer's Offers tab only
  // appears once one exists, and the client sees its status at every stage of the
  // work order. Same query as the tab and the action bar, so they share one request.
  // Only a customer-raised ticket can have one, so a client only asks for those.
  const isCustomerRaised = workOrderDetail?.createdBy?.role?.role === "CUSTOMER";
  const { data: customerCounterOfferData } = useQuery({
    queryKey: ["customerCounterOffer", workOrderDetail?.id],
    queryFn: () => fetchCustomerCounterOffer(workOrderDetail.id),
    enabled:
      !!workOrderDetail?.id &&
      (authRole === "CUSTOMER" || (authRole === "CLIENT" && isCustomerRaised)),
  });
  const hasProposal = !!customerCounterOfferData?.data;

  // The client's banner reflects the offer's latest history row: the send while it
  // is pending, the customer's answer once given.
  const counterOfferStatusAlert = (() => {
    const counterOffer = customerCounterOfferData?.data;
    if (authRole !== "CLIENT" || !counterOffer) return null;
    const latest = counterOffer.offerHistory?.[counterOffer.offerHistory.length - 1];
    const by = [latest?.createdBy?.firstName, latest?.createdBy?.lastName]
      .filter(Boolean)
      .join(" ");
    const on = latest?.createdAt ? dayjs(latest.createdAt).format("MMM D, YYYY [at] h:mm A") : "";

    // The customer can only answer while the ticket awaits approval, so a counter
    // still pending once it has moved on was never answered.
    if (
      counterOffer.status === "PENDING" &&
      workOrderDetail?.status !== WorkOrderStatusType.PENDING_APPROVAL
    ) {
      return {
        type: "info",
        title: "Offer Not Answered",
        message: `The customer did not respond to the offer${by ? ` sent by ${by}` : ""}${on ? ` on ${on}` : ""} before the work order moved on from review.`,
      };
    }
    if (counterOffer.status === "PENDING") {
      return {
        type: "warning",
        title: "Offer Sent",
        message: `Awaiting the customer's response to the offer${by ? ` sent by ${by}` : ""}${on ? ` on ${on}` : ""}.`,
      };
    }
    // An accepted counter offer is stored as ASSIGNED.
    if (counterOffer.status === "ASSIGNED") {
      return {
        type: "success",
        title: "Offer Accepted",
        message: `${by || "The customer"} accepted the offer${on ? ` on ${on}` : ""}. The customer rates have been updated.`,
      };
    }
    if (counterOffer.status === "REJECTED") {
      return {
        type: "error",
        title: "Offer Declined",
        message: `${by || "The customer"} declined the offer${on ? ` on ${on}` : ""}. The work order keeps its original rates.`,
      };
    }
    if (counterOffer.status === "WITHDRAWN") {
      return {
        type: "info",
        title: "Offer Withdrawn",
        message: `${by || "The offer"}${by ? " withdrew the offer" : " was withdrawn"}${on ? ` on ${on}` : ""}. The work order keeps its original rates.`,
      };
    }
    return null;
  })();

  const handleConfirmRemoveFromList = async () => {
    try {
      const payload = {
        workOrderId: workOrderDetail.id,
        status: "DECLINED",
        ...(userAllowedAction?.routedOfferId && { offerId: userAllowedAction.routedOfferId }),
      };
      await declineOfferMutation(payload);
      setRemoveFromListConfirmOpen(false);
      const removedFilter =
        userAllowedAction?.isRouted || userAllowedAction?.isAccepted
          ? "ROUTED"
          : userAllowedAction?.isCounter
            ? "COUNTER"
            : userAllowedAction?.isRequested
              ? "REQUESTED"
              : null;
      navigate(removedFilter ? `/jobs?filter=${removedFilter}` : "/jobs");
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Unable to remove from list",
          type: "error",
        }),
      );
    }
  };

  // Extract WorkOrder route and its childs
  const workOrderRoute = routes.find((route) => route.path === "/workOrder");

  const allTabs = workOrderRoute?.childs || [];

  const assignedToAnotherEngineer =
    authRole === "FREELANCER" &&
    userAllowedAction?.isWorkOrderAlreadyAssigned &&
    !userAllowedAction?.isDeclined &&
    (userAllowedAction?.isRequested ||
      userAllowedAction?.isCounter ||
      userAllowedAction?.isRouted ||
      userAllowedAction?.isAccepted);

  const filteredTabs = allTabs?.filter((tab) => {
    const isFreelancer = authRole === "FREELANCER";
    const isAssignedMember = userAllowedAction?.isAssignedMember;

    if (isFreelancer && isAssignedMember && tab?.text === "Payment") {
      return false;
    }

    const shouldShowMessageTab =
      userAllowedAction?.isWorkOrderAlreadyAssigned ||
      userAllowedAction?.isRouted ||
      userAllowedAction?.isAccepted ||
      userAllowedAction?.isChatInitiated ||
      userAllowedAction?.isAssignedMember ||
      // The tech the work order is assigned to. None of the flags above cover a
      // direct tech: their offer ends up ASSIGNED (not ACCEPTED), they are in no
      // freelance company, and isWorkOrderAlreadyAssigned means "assigned to
      // someone else". So until a client messaged them they had no Messages tab
      // at all — and the work order activity chat was unreachable.
      userAllowedAction?.isAssignedEngineer;

    if (isFreelancer && tab?.text === "Messages" && !shouldShowMessageTab) {
      return false;
    }

    if (
      authRole === "CUSTOMER" &&
      tab?.text === "Messages" &&
      !userAllowedAction?.isWorkOrderChatParticipant
    ) {
      return false;
    }

    if (tab?.text === "Offers" && !hasProposal) {
      return false;
    }

    if (assignedToAnotherEngineer && (tab?.text === "Job Sign-Off" || tab?.text === "Feedback")) {
      return false;
    }

    if (isFreelancer && tab?.text === "Feedback" && !userAllowedAction?.isEngineerFeedbackVisible) {
      return false;
    }
    if (
      authRole === "CUSTOMER" &&
      tab?.text === "Job Sign-Off" &&
      [WorkOrderStatusType.WORKDONE].includes(workOrderDetail.status)
    ) {
      return false;
    }

    return tab?.roles?.includes(authRole) && tab?.statuses?.includes(workOrderDetail.status);
  });
  // Set initial tab based on the current URL
  useEffect(() => {
    const currentTabIndex = filteredTabs.findIndex((tab) =>
      location.pathname.includes(tab.path.split("/:id")[0]),
    );
    if (currentTabIndex !== -1) {
      setValue(currentTabIndex);
    }
  }, [location.pathname, filteredTabs]);
  const chatRef = useRef(null);
  const handleChange = (event, newValue) => {
    if (chatRef.current?.saveDraft) {
      chatRef.current.saveDraft();
    }
    const selectedTab = filteredTabs[newValue];
    // engineerId only applies to the Messages tab (it selects an engineer's chat).
    // Drop it from the URL when switching away to any other tab.
    const searchParams = new URLSearchParams(location.search);
    if (selectedTab.text !== "Messages") {
      searchParams.delete("engineerId");
    }
    // The `tab` sub-tab param only belongs to the Providers tab. Default it to
    // "nearby" when switching into Providers, and drop it for every other tab so a
    // stale value doesn't linger in the URL.
    if (selectedTab.text === "Providers") {
      if (!searchParams.has("tab")) {
        searchParams.set("tab", "nearby");
      }
    } else {
      searchParams.delete("tab");
    }
    const search = searchParams.toString();
    navigate(`/workOrder/${selectedTab.path.replace(":id", id)}${search ? `?${search}` : ""}`);
    setValue(newValue);
  };
  return (
    <BaseWrapper>
      <Box sx={{ width: "100%" }}>
        {workOrderDetail?.notes?.length > 0 && authRole === "CLIENT" && (
          <div className="pt-3">
            <BaseAlert
              title="Note:"
              message={<div style={{ whiteSpace: "pre-line" }}>{workOrderDetail?.notes}</div>}
              type="note"
              closable={false}
            />
          </div>
        )}
        {counterOfferStatusAlert && (
          <div className="pt-3">
            <BaseAlert
              title={counterOfferStatusAlert.title}
              message={counterOfferStatusAlert.message}
              type={counterOfferStatusAlert.type}
              closable={false}
            />
          </div>
        )}
        {workOrderDetail?.status === "CANCELLED" && userAllowedAction?.showCancellationReason && (
          <div className="pt-3">
            <BaseAlert
              title="Cancellation Reason:"
              message={(() => {
                const cancelled = workOrderDetail?.cancelledWorkOrder?.[0];
                if (!cancelled?.cancellationReason) return "No reason provided";
                const label =
                  CANCELLATION_REASON_LABELS[cancelled.cancellationReason] ||
                  cancelled.cancellationReason;
                return cancelled.reason ? `${label}: ${cancelled.reason}` : label;
              })()}
              type="error"
              closable={false}
            />
          </div>
        )}
        {userAllowedAction?.isAccepted && !userAllowedAction?.isWorkOrderAlreadyAssigned && (
          <div className="pt-3">
            <BaseAlert
              title="Work Order Accepted"
              message={
                <div
                  style={{ whiteSpace: "pre-line" }}
                >{`Waiting for the client’s confirmation…`}</div>
              }
              type="info"
              closable={false}
            />
          </div>
        )}

        {assignedToAnotherEngineer && (
          <div className="pt-3">
            <BaseAlert
              title="Work Order Assigned"
              message={
                <div className="flex flex-col items-start gap-3" style={{ whiteSpace: "pre-line" }}>
                  <span>
                    This work order has been assigned to another engineer. You can remove it from
                    your listing.
                  </span>
                  <BaseButton
                    onClick={() => setRemoveFromListConfirmOpen(true)}
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                    className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary min-w-[160px] h-8"
                  >
                    Remove from List
                  </BaseButton>
                </div>
              }
              type="info"
              closable={false}
            />
          </div>
        )}

        {removeFromListConfirmOpen && (
          <BaseConfirmModal
            open={removeFromListConfirmOpen}
            isLoading={isRemoveFromListLoading}
            onClose={() => setRemoveFromListConfirmOpen(false)}
            onConfirm={handleConfirmRemoveFromList}
            confirmText="Remove from List"
            title="Remove from List"
            description={<p>Are you sure you want to remove this work order from your list?</p>}
          />
        )}

        {workOrderDetail?.status === WorkOrderStatusType.INCOMPLETE && (
          <div className="pt-3">
            <BaseAlert
              title={
                IncompleteWorkOrderReasonType[
                  workOrderDetail?.incompleteWorkOrderReason[
                    workOrderDetail?.incompleteWorkOrderReason.length - 1
                  ]?.reasonType || "No reason provided"
                ]
              }
              message={
                workOrderDetail?.incompleteWorkOrderReason[
                  workOrderDetail?.incompleteWorkOrderReason.length - 1
                ]?.reason &&
                `Details: ${
                  workOrderDetail?.incompleteWorkOrderReason[
                    workOrderDetail?.incompleteWorkOrderReason.length - 1
                  ]?.reason
                }`
              }
              type="error"
              closable={false}
            />
          </div>
        )}

        <Box>
          <Tabs
            TabIndicatorProps={{
              style: {
                display: "none",
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            sx={{
              // The global `.MuiTabs-flexContainer { overflow: scroll }` rule adds
              // a second scrollbar inside the scrollable variant - let MUI own it.
              "& .MuiTabs-flexContainer": { overflow: "visible !important" },
              // A disabled scroll button still reserves width, which indents the
              // first tab away from the page edge.
              "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0, opacity: 0 },
            }}
          >
            {filteredTabs.map((tab, index) => (
              <CustomTab
                key={index}
                label={tab.text}
                {...a11yProps(index)}
                sx={{
                  minHeight: "34px",
                  marginRight: "30px",
                }}
              />
            ))}
          </Tabs>
        </Box>
        {filteredTabs.map((tab, index) =>
          tab.text === "Messages" ? (
            <div
              key={index}
              role="tabpanel"
              hidden={value !== index}
              id={`simple-tabpanel-${index}`}
              aria-labelledby={`simple-tab-${index}`}
            >
              <Box>
                <ForwardedChatTab
                  workOrderDetail={workOrderDetail}
                  isActive={value === index}
                  isVisible={value === index}
                  ref={chatRef}
                />
              </Box>
            </div>
          ) : (
            <CustomTabPanel value={value} index={index} key={index}>
              {React.cloneElement(tab.component, { workOrderDetail })}
            </CustomTabPanel>
          ),
        )}
      </Box>
    </BaseWrapper>
  );
};

export default WorkJobTabs;

const ForwardedChatTab = React.forwardRef((props, ref) => {
  return React.cloneElement(<ChatTab />, { ...props, ref });
});
